import React, { useEffect, useRef, useState } from "react";
import Header from "./Header";
import SubHeader from "./SubHeader";
import DataTable, { formatNum } from "./DataTable";
import TableLoader from "../TableLoader";
import Apiauth from "../../../API/Apiauth";
import MetalChart from "./graph/MetalChart";
import { useTranslation } from "react-i18next";

function mapRows(apiData = []) {
  return apiData.map((row) => ({
    collection: row.collection_name,
    inwardQty: row.inward_qty,
    amountInTagPrice: row.amount_in_tag_price,
    soldQty: row.qty,
    tagPrice: row.tag_price,
    sellingPrice: row.selling_price,
    currentStock: row.current_stock,
    currentStockTagPrice: row.current_stock_amount_tag_price,
    ast: row.amount_sell_through_pct,
    sw: row.sales_weight,
    rotationOfWeek: row.rotation_of_week,
  }));
}

function mapTotals(apiTotals = {}, hasData = false) {
  return {
    inwardQty: apiTotals.inward_qty,
    amountInTagPrice: apiTotals.amount_in_tag_price,
    soldQty: apiTotals.qty,
    tagPrice: apiTotals.tag_price,
    sellingPrice: apiTotals.selling_price,
    currentStock: apiTotals.current_stock,
    currentStockTagPrice: apiTotals.current_stock_amount_tag_price,
    ast: apiTotals.amount_sell_through_pct,
    sw: hasData ? 100 : 0,
    rotationOfWeek: apiTotals.rotation_of_week,
  };
}

function mapMetrics(summary = {}) {
  return [
    { label: "Collection", value: formatNum(summary.total_collections) },
    { label: "Qty", value: formatNum(summary.total_qty) },
    { label: "Top Selling Collection (by Amount)", value: summary.top_collection_by_amount },
    { label: "Top Selling Collection (by Qty)", value: summary.top_collection_by_qty ?? "-" },
  ];
}

const DEFAULT_COLUMNS = [
  { key: "_index", label: "#", align: "center", width: 60 },
  { key: "collection", label: "Collection", align: "left", width: 230 },
  { key: "inwardQty", label: "Qty In", align: "center", width: 100 },
  { key: "amountInTagPrice", label: "Qty In Tag Price", align: "center", width: 180 },
  { key: "soldQty", label: "Qty Sold", align: "center", width: 100 },
  { key: "tagPrice", label: "Tag Price", align: "center", width: 180 },
  { key: "sellingPrice", label: "Selling Price", align: "center", width: 180 },
  { key: "currentStock", label: "Current Stock", align: "center", width: 180 },
  { key: "currentStockTagPrice", label: "Stock Tag Price", align: "center", width: 180 },
  { key: "ast", label: "Amount sell-through %", align: "center", width: 200 },
  { key: "sw", label: "Sales weight", align: "center", width: 200 },
  { key: "rotationOfWeek", label: "Rotation of Week(week of supply)", align: "center", width: 260 },
];


const DEFAULT_PAGE_SIZE = 10;
const CHART_MAX_LIMIT = 100000;

const Collection = () => {
  const { t } = useTranslation()
   const [rows, setRows] = useState([]);
   const [chartRows, setChartRows] = useState([]);
   const [totals, setTotals] = useState(null);
   const [metrics, setMetrics] = useState([]);
   const [loading, setLoading] = useState(true);
   const [filter, setFilter] = useState({});
   const [appliedFilters, setAppliedFilters] = useState({});
   const [totalRecords, setTotalRecords] = useState(0);
   const [page, setPage] = useState(1);
   const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
   const [showCharts, setShowCharts] = useState(false);

 const fetchCollectionReport = ({
    page: p = 1,
    limit: l = pageSize,
    ...rest
  } = {}) => {
    setLoading(true);
    Apiauth({ page: p, limit: l, ...rest }, "Inventory/reports/salesByCollection")
      .then((res) => {
        if (res?.code === 200) {
          setRows(mapRows(res.data));
          setTotals(mapTotals(res.totals, Array.isArray(res.data) && res.data.length > 0));
          setMetrics(mapMetrics(res.summary));
          setFilter(res.filters);
          setTotalRecords(res?.pagination?.total ?? 0);
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchChartReport = (filters = {}) => {
    setLoading(true);
    Apiauth(
      { page: 1, limit: CHART_MAX_LIMIT, ...filters },
      "Inventory/reports/salesByCollection",
    )
      .then((res) => {
        if (res?.code === 200) {
          setChartRows(mapRows(res.data));
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCollectionReport({ page: 1, limit: DEFAULT_PAGE_SIZE });
  }, []);

  useEffect(() => {
    if (showCharts) {
      fetchChartReport(appliedFilters);
    }
  }, [showCharts, appliedFilters]);

  function handleFetchData({ page: newPage, limit: newLimit }) {
    if (newLimit !== pageSize) setPageSize(newLimit);
    setPage(newPage);
    fetchCollectionReport({ ...appliedFilters, page: newPage, limit: newLimit });
  }

  function handleFilterApply(filterParams = {}) {
    setAppliedFilters(filterParams);
    setPage(1);
    fetchCollectionReport({ ...filterParams, page: 1, limit: pageSize });
  }


  return (
    <>
   <Header title={`${t("Sales by Collection")}`} metrics={metrics}/>
  <SubHeader
       filters={filter} showCharts={showCharts}  setShowCharts={setShowCharts} onFilterApply={handleFilterApply}
      />
      {loading ? (
        <TableLoader />
      ) : (
        showCharts?(<MetalChart rows={chartRows} text="Collection"/>):
      (  <DataTable
          columns={DEFAULT_COLUMNS}
          data={rows}
          totals={totals}
          showSearch={true}
          showDateRange={true}
          pageLimit={pageSize}
          totalRecords={totalRecords}
          currentPage={page}
          onPageChange={setPage}
          fetchData={handleFetchData}
        />)
      )}
   </>
  )
}

export default Collection